"use client";
import Image from "next/image";
import React, { useState, useRef, useEffect, useMemo } from "react";
import AddIcon from "@mui/icons-material/Add"; // Import the Add icon from Material UI
import useImageUrlModifier from "@/lib/useImageUrlModifier";

const ImageMagnifier = ({ src, initialZoomLevel = 2 }) => {
  const modifyImageUrl = useImageUrlModifier();
  const [isHovered, setIsHovered] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(initialZoomLevel);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const magnifierSize = 120;
  const fallbackImage = "/images/dummy-image-square.jpg"; // Fallback image

  // Optimized mouse move handler with bounding logic
  const handleMouseMove = (e) => {
    const imageRect = imageRef.current?.getBoundingClientRect();
    if (!imageRect) return;

    let x = e.pageX - imageRect.left - window.scrollX;
    let y = e.pageY - imageRect.top - window.scrollY;

    // Prevent magnifier box from overflowing outside the image
    x = Math.max(
      magnifierSize / 2,
      Math.min(x, imageRect.width - magnifierSize / 2)
    );
    y = Math.max(
      magnifierSize / 2,
      Math.min(y, imageRect.height - magnifierSize / 2)
    );

    setCursorPosition({ x, y });
  };

  // Optimized zoom handling, with a scroll wheel event that adjusts zoom
  const handleZoomScroll = (e) => {

    setZoomLevel((prevZoom) => {
      let newZoomLevel = prevZoom;
      if (e.deltaY < 0 && prevZoom < 5) newZoomLevel = prevZoom + 0.1;
      if (e.deltaY > 0 && prevZoom > 1) newZoomLevel = prevZoom - 0.1;
      return newZoomLevel;
    });
  };

  // Memoized styles for the zoomed background
  const zoomedImageStyle = useMemo(() => {
    if (!imageRef.current) return {};
    return {
      backgroundImage: `url(${src})`,
      backgroundSize: `${zoomLevel * 100}%`,
      backgroundPosition: `${((cursorPosition.x - magnifierSize / 2) / imageRef.current.width) * 100
        }% ${((cursorPosition.y - magnifierSize / 2) / imageRef.current.height) * 100
        }%`,
    };
  }, [zoomLevel, cursorPosition, src]);

  // Prevent scroll when hovering over the image
  useEffect(() => {
    const preventScroll = (e) => e.preventDefault();

    if (isHovered) {
      document.addEventListener("wheel", preventScroll, { passive: false });
    } else {
      document.removeEventListener("wheel", preventScroll);
    }

    return () => {
      document.removeEventListener("wheel", preventScroll);
    };
  }, [isHovered]);



  return (
    <div className="flex">
      {/* Original Image */}
      <div
        className="relative z-[5] flex border border-secondary justify-center w-full  aspect-square"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onWheel={handleZoomScroll} // Zoom adjustment on scroll
      >
        <Image
          layout="responsive"
          width={1}
          height={1}
          ref={imageRef}
          src={modifyImageUrl(src, 500, 500)}
          alt="Zoomable"
          className="object-cover w-full aspect-square"
          placeholder="blur"
          blurDataURL={fallbackImage} // Low-quality version for blur
          sizes="auto"
        />

        {/* Rectangular magnifier box */}
        {isHovered && (
          // Inside your magnifier box div:
          <div
            className="absolute z-[99999] pointer-events-none border border-gray-400 flex justify-center items-center"
            style={{
              width: `${magnifierSize}px`,
              height: `${magnifierSize}px`,
              left: `${cursorPosition.x - magnifierSize / 2}px`,
              top: `${cursorPosition.y - magnifierSize / 2}px`,
              backgroundColor: "white",
              opacity: 0.5, // Semi-transparent for visibility
              willChange: "transform", // Hint to the browser that this will change
            }}
          >
            <AddIcon style={{ fontSize: 30, color: "gray" }} />{" "}
          </div>
        )}
      </div>

      {/* Zoomed Image Display */}
      {isHovered && (
        <div
          className="ltr:ml-4 rtl:mr-4 w-full min-h-[300px] aspect-square border-2 border-secondary absolute rtl:right-[100%] ltr:left-[100%] z-[99999]"
          style={zoomedImageStyle}
        />
      )}
    </div>
  );
};

export default ImageMagnifier;
